import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  SALE_SOURCES,
  SALE_SOURCE_LABELS,
  type Customer,
  type Paginated,
  type PaymentMethod,
  type PaymentStatus,
  type SaleSource,
} from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { useRecordLocation } from '../../lib/locationContext';
import { LocationField } from '../../components/LocationField';
import { toDateTimeLocal } from '../../lib/format';
import { Button, Input, Select, Textarea } from '../../components/ui';

/**
 * Recording a sale.
 *
 * The form is ordered by how a person actually thinks about a sale: how much,
 * what for, who from, how they paid. Cost price is tucked into an optional
 * section — most businesses will not fill it in, and reports handle its
 * absence honestly rather than pretending margin is 100%.
 */
export function SaleForm({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const timezone = business?.timezone ?? 'UTC';

  const [form, setForm] = useState({
    amount: '',
    description: '',
    customerId: '',
    paymentMethod: 'cash' as PaymentMethod,
    paymentStatus: 'paid' as PaymentStatus,
    amountPaid: '',
    source: 'manual' as SaleSource,
    costAmount: '',
    occurredAt: toDateTimeLocal(new Date().toISOString(), timezone),
    notes: '',
  });
  const [showCost, setShowCost] = useState(false);
  // A sale happened in a place. The API refuses one that does not say where.
  const location = useRecordLocation();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const { data: customers } = useQuery({
    queryKey: ['customers', business?.businessId, 'picker'],
    queryFn: () => api.get<Paginated<Customer>>(path('/customers?pageSize=200&sortBy=name')),
    enabled: Boolean(business),
  });

  const update =
    <K extends keyof typeof form>(key: K) =>
    (event: { target: { value: string } }) =>
      setForm((current) => ({ ...current, [key]: event.target.value }));

  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.post(path('/sales'), payload),
    onSuccess: onDone,
    onError: (caught) => {
      if (caught instanceof ApiError) {
        setError(caught.message);
        if (caught.fields) {
          setFieldErrors(
            Object.fromEntries(
              Object.entries(caught.fields).map(([key, messages]) => [key, messages[0] ?? '']),
            ),
          );
        }
      } else {
        setError('Could not record the sale.');
      }
    },
  });

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    if (!location.branchId) {
      setFieldErrors({ branchId: 'Choose where this sale happened.' });
      return;
    }

    create.mutate({
      branchId: location.branchId,
      amount: form.amount,
      description: form.description || undefined,
      customerId: form.customerId || null,
      paymentMethod: form.paymentMethod,
      paymentStatus: form.paymentStatus,
      // Only meaningful for a partial payment; the API derives it otherwise.
      ...(form.paymentStatus === 'partial' && form.amountPaid
        ? { amountPaid: form.amountPaid }
        : {}),
      source: form.source,
      costAmount: showCost && form.costAmount ? form.costAmount : null,
      occurredAt: new Date(form.occurredAt).toISOString(),
      notes: form.notes || undefined,
    });
  }

  const currency = business?.currencyCode ?? '';

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {error && !Object.keys(fieldErrors).length && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <LocationField
        value={location.branchId}
        onChange={location.setBranchId}
        locations={location.locations}
        error={fieldErrors['branchId']}
      />

      <Input
        label={`Amount (${currency})`}
        required
        autoFocus
        // `inputMode="decimal"` gives a phone the numeric keypad without the
        // spinner arrows a number input adds on desktop.
        inputMode="decimal"
        placeholder="0.00"
        value={form.amount}
        onChange={update('amount')}
        error={fieldErrors.amount}
      />

      <Input
        label="Description"
        value={form.description}
        onChange={update('description')}
        error={fieldErrors.description}
        placeholder="What was sold?"
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Select
          label="Customer"
          placeholder="Walk-in / not recorded"
          options={(customers?.data ?? []).map((customer) => ({
            value: customer.id,
            label: customer.name,
          }))}
          value={form.customerId}
          onChange={update('customerId')}
          error={fieldErrors.customerId}
        />

        <Select
          label="Source"
          options={SALE_SOURCES.map((value) => ({ value, label: SALE_SOURCE_LABELS[value] }))}
          value={form.source}
          onChange={update('source')}
          error={fieldErrors.source}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Select
          label="Payment method"
          required
          options={PAYMENT_METHODS.map((value) => ({
            value,
            label: PAYMENT_METHOD_LABELS[value],
          }))}
          value={form.paymentMethod}
          onChange={update('paymentMethod')}
          error={fieldErrors.paymentMethod}
        />

        <Select
          label="Payment status"
          required
          // Refunded and void are outcomes of a later action, not something you
          // record a brand-new sale as.
          options={[
            { value: 'paid', label: 'Paid in full' },
            { value: 'partial', label: 'Partially paid' },
            { value: 'unpaid', label: 'Unpaid' },
          ]}
          value={form.paymentStatus}
          onChange={update('paymentStatus')}
          error={fieldErrors.paymentStatus}
        />
      </div>

      {form.paymentStatus === 'partial' && (
        <Input
          label={`Amount received (${currency})`}
          required
          inputMode="decimal"
          placeholder="0.00"
          value={form.amountPaid}
          onChange={update('amountPaid')}
          error={fieldErrors.amountPaid}
          hint="The rest is tracked as outstanding"
        />
      )}

      <Input
        label="Date and time"
        type="datetime-local"
        value={form.occurredAt}
        onChange={update('occurredAt')}
        error={fieldErrors.occurredAt}
      />

      {showCost ? (
        <Input
          label={`Cost of goods (${currency})`}
          inputMode="decimal"
          placeholder="0.00"
          value={form.costAmount}
          onChange={update('costAmount')}
          error={fieldErrors.costAmount}
          hint="Optional. Enables gross profit in reports for this sale."
        />
      ) : (
        <button
          type="button"
          onClick={() => setShowCost(true)}
          className="text-sm font-medium text-brand-600 hover:text-brand-700"
        >
          + Add cost price (for profit tracking)
        </button>
      )}

      <Textarea
        label="Notes"
        value={form.notes}
        onChange={update('notes')}
        error={fieldErrors.notes}
      />

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={create.isPending}>
          Record sale
        </Button>
      </div>
    </form>
  );
}
